import { SendOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

const MAX_QUESTION_CHARS = 4000; // BE `AskIn.question` max_length
const MAX_TEXTAREA_PX = 180;

// Полето за въпрос: Enter — питай, Shift+Enter — нов ред. Расте с текста до
// MAX_TEXTAREA_PX, после скролва. Докато се чака отговор, полето остава
// активно (може да се пише следващият въпрос), но изпращането — не.
export const AssistantComposer = ({
  onAsk,
  busy,
  isNarrow,
}: {
  onAsk: (question: string) => void;
  busy: boolean;
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Авто-височина: височината зависи от съдържанието — стойност по време на
  // изпълнение, затова е през style на DOM елемента, не клас.
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, MAX_TEXTAREA_PX)}px`;
  }, [question]);

  const submit = () => {
    const trimmed = question.trim();
    if (!trimmed || busy) return;
    onAsk(trimmed);
    setQuestion("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // isComposing: Enter, който довършва дума от IME, не е „изпрати".
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const canSend = question.trim().length > 0 && !busy;

  return (
    <div
      className={`flex items-end gap-[10px] rounded-[14px] border border-shade-3 bg-surface transition-colors duration-150 focus-within:border-brand-ink ${
        isNarrow ? "px-[10px] py-[8px]" : "px-[14px] py-[10px]"
      }`}
    >
      <textarea
        ref={textareaRef}
        value={question}
        onChange={(event) => setQuestion(event.target.value)}
        onKeyDown={onKeyDown}
        maxLength={MAX_QUESTION_CHARS}
        rows={1}
        autoFocus={!isNarrow}
        // На телефон подсказката за Enter не се побира на един ред
        placeholder={t(isNarrow ? "monitoring.assistantPlaceholderShort" : "monitoring.assistantPlaceholder")}
        aria-label={t("monitoring.assistantPlaceholder")}
        // 16px на телефон: под 16px iOS Safari зуумва при фокус
        className={`max-h-[180px] min-h-[24px] flex-1 resize-none border-none bg-transparent p-0 py-[5px] font-[inherit] leading-[1.5] text-ink outline-none placeholder:text-muted ${
          isNarrow ? "text-[16px]" : "text-[14px]"
        }`}
      />
      <button
        type="button"
        onClick={submit}
        disabled={!canSend}
        aria-label={t("monitoring.assistantSend")}
        className={`inline-flex h-[36px] shrink-0 cursor-pointer items-center gap-[6px] rounded-[10px] border-none px-[14px] text-[13px] font-semibold transition-colors duration-150 disabled:cursor-default ${
          canSend ? "bg-brand text-white hover:bg-brand-deep" : "bg-fill text-muted"
        }`}
      >
        <SendOutlined className="!text-[13px]" />
        {!isNarrow && t("monitoring.assistantSend")}
      </button>
    </div>
  );
};
